/**
 * **One propeller per aeroplane.** Which of the two the port has for an enhanced
 * airframe actually gets drawn.
 *
 * There are two, and both are right in their own place. AD-012's effect
 * (`propeller-discs.ts`) draws a shimmer disc and blade silhouettes on the
 * measured hub of every aeroplane in the sky, in *every* regime — a stopped
 * propeller really does stop and a wreck shows still blades, so the effect is
 * not a "fast" representation that leaves a gap at rest. The enhanced `.glb`
 * carries a **modelled** propeller as well, recovered from the original's own
 * line nodes (`tools/enhanced/src/propeller.ts`) and built as real geometry
 * (`tools/blender/rb_enhanced/rigging.py`). Drawn together they are two
 * propellers in the same place, which is what the user reported on 2026-08-24.
 *
 * So: **the modelled blade is drawn exactly when the effect is not drawing one
 * for that aeroplane.** That is not the same as "when PROPELLER EFFECT is off",
 * and the difference is `hasNoseProp` below — the effect deliberately declines
 * three airframes whose propellers are not on the nose, and those three would
 * otherwise be left with no propeller at all in the state that is meant to have
 * the most. On an F.E.2b, a Gotha and a Handley Page the modelled blades are the
 * only propeller there is, and they stay.
 *
 * The rule lives here, apart from both halves, because both halves need it and
 * neither may import the other: `propeller-discs.ts` already reads
 * `aircraft-mesh.ts` for its hubs, so `aircraft-mesh.ts` reading the effect's
 * own module back would be a cycle.
 */

/**
 * Baked model basenames with no propeller at the nose — `FE2` is a pusher,
 * `GOTHA` carries twin pushers on wing nacelles and `HANDLEY` twin tractors on
 * the same, so a measured *nose* hub would put AD-012's disc in the wrong place
 * and the effect skips them by name (`propeller-discs.ts`'s module doc has the
 * long form).
 *
 * **`AIRCO` joins them here, and the enhanced pipeline is what proved it.** The
 * Airco D.H.2 is a pusher — `tools/enhanced/src/propeller.ts` names it as one in
 * its opening paragraph — and the roster missed it because nothing on the
 * runtime side had ever measured where its propeller is. The recovered profile
 * has: `AIRCO`'s blades are drawn at model `y = -1.63`, which is **0.32 m
 * forward of its bounding-box centre**, while `propellerHubOrFallback` puts the
 * effect's disc at **3.19 m** forward — the raw nose tip, because the D.H.2 is
 * modelled with a single-vertex spike there and the hub measurement declines it
 * (`packages/baker/src/propeller-hub.ts`). Those are 2.9 m apart on a 6.4 m
 * aeroplane: the disc was on the nose of a machine whose engine is behind the
 * pilot. The same comparison puts the other three at 4.7 m, 6.7 m and 10.0 m out
 * and every tractor in the fleet within 0.35 m, so the four sort themselves.
 *
 * The consequence is the good one: on all four the `.glb`'s own modelled blades
 * are now the propeller, in the right place, in both toggle states.
 */
const NO_NOSE_PROP: ReadonlySet<string> = new Set(["AIRCO", "FE2", "GOTHA", "HANDLEY"]);

/** Whether AD-012's effect draws a disc for this model id at all. */
export function hasNoseProp(modelId: string): boolean {
  return !NO_NOSE_PROP.has(modelId.slice(modelId.lastIndexOf("/") + 1));
}

/**
 * Whether the enhanced `.glb`'s own modelled propeller should be drawn for
 * `modelId`, given whether the effect is running on this screen.
 *
 * `propellerEffect` is the *screen's* answer, not the setting's: the career
 * turntable and `/assets`' model viewer have no effect layer at all and pass
 * `false`, which is why an enhanced aeroplane on a stand keeps the blades it was
 * built with.
 */
export function modelledPropellerVisible(modelId: string, propellerEffect: boolean): boolean {
  return !propellerEffect || !hasNoseProp(modelId);
}
